// src/components/tabs/removals.js
import React, { useMemo } from "react";
import { scenarioKeyForBackend, CITIZENS_PANEL_KEY, STANDARD_SCENARIO_SUFFIX } from "../../utils/scenarios";
import CarbonRemovalTechnologies from "../dashboard/CarbonRemovalTechnologies";
import { useCdrBuckets } from "../../hooks/useCdrBuckets";

export default function Removals({
  scenarioKey,
  carbonRemovalComparisonScenario,            // { scenario, variant } | null
  setCarbonRemovalComparisonScenario,         // fn
  from = 2025,
  to = 2050,
  step = 5,
  unitScale = 1,                               // adjust if your DB needs scaling
}) {

    const parseFamily = (key) => {
  const hasDVL = key?.includes("_dvl_");
  const base = hasDVL ? "net_zero_futures_dvl" : "net_zero_futures";
  const suffix = key?.match(/_(\d{4})$/)?.[0] ?? ""; // e.g. "_2208" | "_0509"
  return { base, suffix };
};
const buildKey = (base, name, ambition, suffix) => `${base}_${name}_${ambition}${suffix}`;

  // Build backend key for comparison (optional overlay)
  const comparisonScenarioKey = useMemo(() => {
    if (!carbonRemovalComparisonScenario) return undefined;
    const { scenario, variant } = carbonRemovalComparisonScenario;
    // Special case for citizens-panel
    if (`${scenario}-${variant}` === "citizens-panel") {
      return CITIZENS_PANEL_KEY;
    }
    const { base } = parseFamily(scenarioKey);
    const name = scenario;          // "current" | "steer" | "shift" | "transform"
    const ambition = variant ?? "low"; // "low" | "high"
    // Use standard suffix for comparison scenarios (not the primary scenario's suffix)
    return buildKey(base, name, ambition, STANDARD_SCENARIO_SUFFIX);
  }, [carbonRemovalComparisonScenario, scenarioKey]);

  // For the insight card, pull the same bucketed data the chart uses
  const { labels, datasets } = useCdrBuckets({
    scenario: scenarioKey,
    from,
    to,
    step,
    unitScale,
    includeTotal: false,   // we’ll sum buckets ourselves
  });

  const { lastYear, lastTotal } = useMemo(() => {
    if (!labels?.length || !datasets?.length) return { lastYear: null, lastTotal: null };
    const idx = labels.length - 1;
    const total = datasets.reduce((sum, ds) => sum + (ds.data?.[idx] ?? 0), 0);
    return { lastYear: labels[idx], lastTotal: total };
  }, [labels, datasets]);

  // normalise dropdown value
    const selectedComparisonValue = carbonRemovalComparisonScenario
    ? `${carbonRemovalComparisonScenario.scenario}-${carbonRemovalComparisonScenario.variant ?? "low"}`
    : "";

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-3">Carbon Dioxide Removals</h2>
        <p className="text-sm text-gray-600 mb-4 max-w-2xl mx-auto">
            Explore the role and limitations of ways to actively remove CO₂ from the atmosphere.
        </p>
        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-100 inline-block max-w-md mx-auto">
          <h3 className="text-sm font-medium text-orange-800">Important Context</h3>
          <p className="text-xs mt-1">
           Carbon dioxide removal methods are still developing. They are not yet widely deployed, remain expensive, have implications in terms of land use, and uncertainty about how well they will work.
          </p>
        </div>
      </div>

      {/* Carbon Removal Technologies Section */}
      <div className="bg-white p-5 rounded-lg mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">
          Carbon Removal Technologies Projection
        </h3>

        <CarbonRemovalTechnologies
          scenario={scenarioKey}
          comparisonScenarioKey={comparisonScenarioKey}
          comparisonScenario={comparisonScenarioKey}
          from={from}
          to={to}
          step={step}
          showTotal={false}
          unitScale={0.001}
        />

      {/* Comparison Scenario Dropdown */}
        <div className="mb-4 mt-4 flex items-center justify-center gap-3 w-full sm:w-4/5 lg:w-3/5 m-auto">
          <label className="text-xs text-gray-600">Add additional scenario:</label>
          <select
            value={selectedComparisonValue}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return setCarbonRemovalComparisonScenario(null);
              const [scenario, variant = "low"] = v.split("-");
              setCarbonRemovalComparisonScenario({ scenario, variant });
            }}
            className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">None</option>
            {/* <option value="current-low">Current</option> */}
                <option value="steer-low">Less change + Steer</option>
                <option value="shift-low">Less change + Shift</option>
                <option value="transform-low">Less change + Transform</option>
                <option value="steer-high">More change + Steer</option>
                <option value="shift-high">More change + Shift</option>
                <option value="transform-high">More change + Transform</option>
                 <option value="citizens-panel">Citizens Panel</option>
          </select>
        </div>

        {/* Insight card */}
        <div className="mt-4 bg-gray-50 p-3 rounded-lg border w-full sm:w-4/5 lg:w-3/5 m-auto">
          <h4 className="text-xs font-medium text-gray-700 mb-2">Key Insights</h4>
         <p className="text-xs text-gray-600">
        Total projected carbon removal by {lastYear ?? to}:
        <span className="font-bold ml-1">
            {lastTotal != null ? (Number(lastTotal) / 1000).toFixed(1) : "—"} Mt CO₂/year
        </span>
        </p>
          <p className="text-xs text-gray-600 mt-1">
           While these technologies may help to balance out emissions that cannot be avoided, they cannot solve the problem on their own. Significant emissions reductions are crucial.
          </p>
        </div>

        <div className="mt-12">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Types of carbon dioxide removal</h3>
          <p className="text-sm text-gray-600">
           The methods below are currently widely discussed as promising options for carbon dioxide removal in the UK and globally.
          </p>
        </div>

        {/* Technology Explanations */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {
              name: "Trees and Soils",
              type: "Land-based",
              image: "/reforrestration.jpg",
              description:
                "Planting trees is the most important type of land-based removal in the UK. Trees absorb carbon dioxide through photosynthesis. Carbon is also held in the soil.",
              potentialRemoval: "Carbon dioxide stored in trees and soils could range from 10 to 50 MtCO₂/year. This would mean using between 6.55 and 28.58 thousand km2 to forests and energy crops.",
              challenges: "Lots of land needed, in competition with food production, nature, and energy crops. Planting rates, and slow growing times.",
              risks:
                "Vulnerable to fires, disease, and climate change. Permanent CO2 storage not guaranteed.",
              benefits:
                "Potentially more and better nature, flood and water  management, cleaner air, access to green spaces, soil retention.",
              outcomes:
                "Slow CO₂ removal at first as they begin to grow, but long-term carbon storage in trees and soil.",
            },
            {
              name: "Direct Air Capture (DAC)",
              type: "Engineered",
              image: "/DAC.jpg",
              description:
                "Direct Air Capture is one of the most discussed types of engineered removal. Machines use chemical processes and energy to extract carbon dioxide from the air, before transporting it to long-term underground storage, mostly offshore.",
              potentialRemoval: "Carbon dioxide removed through DAC ranges from 0 to 51 MtCO₂/year in our runs. DAC takes up very little land, but the energy needed to remove 51 MtCO₂/year represents about 27 % of the electricity we produce today.",
              challenges: "High electricity use, current high costs, requires pipelines or other transport solutions to remove and bury the carbon.",
              risks:
                "Extremely high costs (£400-800/tonne CO₂), very high electricity needs, still only a small number of working examples worldwide.",
              benefits:
                "Precise control, permanent removal if stored securely underground, doesn't require  large land area.",
              outcomes:
                "Immediate carbon dioxide capture, requires chemicals and large amounts of energy, and needs to be linked to transport and storage infrastructure to bury the carbon.",
            },
            {
              name: "Bioenergy with Carbon Capture and Storage (BECCS)",
              type: "Engineered + land based",
              image: "/bioenergycapture.jpg",
              description:
                "Growing crops, burning them to produce energy, and capturing and storing the resulting CO₂.",
              potentialRemoval: "CO2 removed through BECCS could range from  to 7 and 56 MtCO₂/year. BECCS plants take up very little land, but they need energy crops which need planting in the UK, or importing from abroad.",
              challenges: "Land availability and competition, balance between causing and removing emissions, social and environmental impacts of growing energy crops in the UK or abroad.",
              risks:
                "Lots of land needed, in competition with food production, nature, and land-based carbon dioxide removals. It can use lots of water, and have social and environmental impacts where the crops are grown. There is uncertainty and complexity around whether, and when, it removes much more emissions than it causes.",
              benefits:
                "BECCS can produce different forms of energy, including electricity, hydrogen, or sometimes also liquid fuels.",
              outcomes:
                "Can, in principle, remove more emissions than it causes if all stages of the process are carefully managed.",
            },
          ].map((technology) => (
            <div key={technology.name} className="bg-gray-50 p-4 rounded-lg border">
              <div className="mb-3 flex justify-center">
                <img
                  src={technology.image}
                  alt={technology.name}
                  className="w-full h-64 object-cover rounded-lg"
                />
              </div>
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-medium text-gray-700">{technology.name}</h4>
                <span
                  className={`text-xs px-2 py-1 rounded ${
                    technology.type === "Natural"
                      ? "bg-green-100 text-green-800"
                      : technology.type === "Natural Enhanced"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {technology.type}
                </span>
              </div>
              <p className="text-xs text-gray-600 mb-3 text-left">{technology.description}</p>

              <div className="space-y-2">
                <div className="border-t pt-2">
                  <div className="text-xs mb-1 text-left">
                    <span className="font-medium text-blue-600">Potential Removal:</span>
                    <span className="ml-1">{technology.potentialRemoval}</span>
                  </div>
                </div>

                <div>
                  <div className="text-xs font-medium text-green-700 mb-1 text-left">Benefits:</div>
                  <p className="text-xs text-gray-600 text-left">{technology.benefits}</p>
                </div>

                <div>
                  <div className="text-xs font-medium text-red-700 mb-1 text-left">Risks:</div>
                  <p className="text-xs text-gray-600 text-left">{technology.risks}</p>
                </div>

                <div>
                  <div className="text-xs font-medium text-purple-700 mb-1 text-left">Expected Outcomes:</div>
                  <p className="text-xs text-gray-600 text-left">{technology.outcomes}</p>
                </div>

                <div className="border-t pt-2">
                  <div className="text-xs text-left">
                    <span className="font-medium text-orange-600">Key Challenges:</span>
                    <span className="ml-1 text-gray-600">{technology.challenges}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
